// R4 laggard-climb (IMPLEMENTATION §0.2 R4, GDD §8.1) for the Loader and the Gunsmith: one set per cycle,
// tier-preserving, no hoppers.
//   if some tier k is present in every slot: consume one Tk per slot (largest such k) → start cycle (out Tk)
//   elif any slot is empty: stall NO_INPUT (soft)
//   else: K = max tier across all slots
//     for each slot in fixed order (1 merge per slot per tick):
//       if the slot has no item of tier K: merge its lowest identical pair below K
//       elif the slot is full:             merge its lowest identical pair below the family max
//     stall MISMATCH (soft)
// Why it cannot deadlock: a slot missing K packs to at most one item per tier below K (≤ 3 items < cap 4),
// so it always takes input, and powers of two carry until it forms a T(K). The Loader calls this once per
// tick at most after a failure (the R2 loop stops on an idle machine), so "1 merge per slot per tick" holds.

import { type MatcherType, matcherDef, recipeOf } from '../bdefs.ts';
import { dropHeld } from '../containers.ts';
import type { Int } from '../core/types.ts';
import type { Ctx } from '../ctx.ts';
import { discover, famDef, newId } from '../entities.ts';
import { raiseStall } from '../stall.ts';
import type { ItemRef, ProcessorB } from '../state.ts';

function hasTier(xs: readonly ItemRef[], t: Int): boolean {
  for (const r of xs) if (r.tier === t) return true;
  return false;
}

function removeFirstOfTier(xs: ItemRef[], t: Int): ItemRef {
  for (let i = 0; i < xs.length; i++) {
    if ((xs[i] as ItemRef).tier === t) return xs.splice(i, 1)[0] as ItemRef;
  }
  throw new Error(`no T${t} in slot`);
}

/** Merges the lowest identical pair below `limit` inside a slot (in place, new id). True if one merged. */
export function mergeLowestPair(c: Ctx, b: ProcessorB, xs: ItemRef[], limit: Int): boolean {
  for (let t = 1; t < limit; t++) {
    let a = -1;
    let k = -1;
    for (let i = 0; i < xs.length; i++) {
      if ((xs[i] as ItemRef).tier !== t) continue;
      if (a < 0) a = i;
      else {
        k = i;
        break;
      }
    }
    if (k < 0) continue;
    const second = xs.splice(k, 1)[0] as ItemRef;
    const first = xs.splice(a, 1)[0] as ItemRef;
    dropHeld(c, first.id);
    dropHeld(c, second.id);
    const isNew = discover(c, first.fam, t + 1);
    const r: ItemRef = { id: newId(c.s), fam: first.fam, tier: t + 1 };
    xs.push(r);
    c.ev.push({
      e: 'merge',
      id: r.id,
      a: first.id,
      b: second.id,
      fam: first.fam,
      tier: t + 1,
      x: b.x,
      y: b.y,
      first: isNew,
      via: 'machine',
    });
    return true;
  }
  return false;
}

/** R4 pickSet: starts a cycle (true) or waits / packs slots and stalls (false). */
export function matcherPick(c: Ctx, b: ProcessorB): boolean {
  const def = matcherDef(c.d, b.type as MatcherType);
  const slots = b.slots;
  let top = 0;
  for (const s of slots) for (const r of s) if (r.tier > top) top = r.tier;
  for (let k = top; k >= 1; k--) {
    let all = true;
    for (const s of slots) {
      if (!hasTier(s, k)) {
        all = false;
        break;
      }
    }
    if (!all) continue;
    for (const s of slots) dropHeld(c, removeFirstOfTier(s, k).id);
    b.proc = { fam: recipeOf(c.d, b).out, tier: k };
    return true;
  }
  for (const s of slots) {
    if (s.length === 0) {
      raiseStall(c, b, 'NO_INPUT');
      return false;
    }
  }
  const fams = def.slots;
  for (let i = 0; i < slots.length; i++) {
    const s = slots[i] as ItemRef[];
    if (!hasTier(s, top)) mergeLowestPair(c, b, s, top);
    else if (s.length >= def.slotCap)
      mergeLowestPair(c, b, s, famDef(c.d, fams[i] as ItemRef['fam']).maxTier);
  }
  raiseStall(c, b, 'MISMATCH');
  return false;
}
